// Helper for OMID error logging
function logOmidError(message: string, error?: unknown, context?: unknown) {
  if (error) {
    console.error(`OMID: ${message}`, error, context || '')
  } else {
    console.error(`OMID: ${message}`, context || '')
  }
}

interface OmidVerificationResource {
  uri: string
  vendor: string
  verificationParameters?: string
  apiFramework?: string
}

interface OmidAd {
  adId: string
  clientReport?: boolean
  durationInSeconds?: number
  adVerifications?: Array<{
    vendor: string
    verificationParameters?: string
    javaScriptResource?: OmidVerificationResource[]
  }>
  trackingEvents?: Array<{
    eventType: string
    eventId: string
    beaconUrls?: string[]
  }>
}

interface OmidTrackingData {
  avails?: Array<{
    ads?: OmidAd[]
  }>
}

export type AdTrackingData = OmidTrackingData

// OMID TypeScript interfaces
interface OmidContext {
  videoElement?: HTMLVideoElement | null
  verificationScriptResources?: unknown[]
  supports?: string[]
  accessMode?: string
  integration?: string
  setServiceWindow?: (win: Window) => void
  serviceWindow?: Window
}

interface OmidAdSession {
  setCreativeType: (type: string) => void
  setImpressionType: (type: string) => void
  start: () => void
  finish?: () => void
  adEvents?: OmidAdEvents
  mediaEvents?: OmidMediaEvents
  registerSessionObserver: (cb: (event: OmidSessionEvent) => void) => void
}

interface OmidAdEvents {
  loaded: () => void
  impressionOccurred: () => void
}

interface OmidMediaEvents {
  start: (duration: number, volume: number) => void
  firstQuartile: () => void
  midpoint: () => void
  thirdQuartile: () => void
  complete: () => void
  pause: () => void
  resume: () => void
  bufferStart: () => void
  bufferFinish: () => void
  volumeChange: (volume: number) => void
}

interface OmidSessionEvent {
  type: string
  data?: unknown
}

class OmidManager {
  private omidSessionRanges = new Map<
    string,
    { end: string; session: unknown }
  >()
  private executedAdJs = new Set<string>()
  private reportedAdEvents = new Set<string>()
  private adTrackingData: OmidTrackingData | null = null
  private videoElement: HTMLVideoElement | null = null
  private cleanedUpAdIds = new Set<string>()

  constructor(videoElement: HTMLVideoElement) {
    this.videoElement = videoElement
  }

  updateVideoElement(videoElement: HTMLVideoElement) {
    this.videoElement = videoElement
  }

  setAdTrackingData(data: OmidTrackingData) {
    this.adTrackingData = data
  }

  /**
   * Handles changes in the video fragment sequence number.
   * - Cleans up OMID sessions that are out of range.
   * - Executes the ad's JavaScript verification resources if not already executed.
   * - Handles tracking events for the ad.
   *
   * @param {string} fragSn - The fragment sequence number to process.
   * @returns {Promise<void>} Resolves when all asynchronous operations are complete.
   */
  handleFragmentChange(fragSn: string) {
    this.cleanupSessionsOutOfRange(fragSn)
    const ad = this.findAdByFragmentSn(fragSn)
    if (!ad) {
      return
    }
    if (!this.executedAdJs.has(ad.adId)) {
      try {
        this.executeAdJavaScriptResource(ad)
        this.executedAdJs.add(ad.adId)
      } catch (err) {
        logOmidError('Failed to execute ad JavaScript resource', err, {
          adId: ad.adId,
        })
      }
    }
    this.handleTrackingEvents(ad, fragSn)
  }

  /**
   * Handles OMID verification.
   * @param ad - The ad object.
   */
  executeAdJavaScriptResource(ad: OmidAd) {
    if (!ad?.adVerifications) {
      return
    }
    let endSn = ad.adId
    if (ad.trackingEvents?.length) {
      endSn = ad.trackingEvents.reduce(
        (max, evt) => (evt.eventId && evt.eventId > max ? evt.eventId : max),
        endSn
      )
    }
    if (!this.omidSessionRanges.has(ad.adId)) {
      this.omidSessionRanges.set(ad.adId, { end: endSn, session: null })
    }
    for (const verification of ad.adVerifications) {
      if (!verification.javaScriptResource?.length) {
        continue
      }
      for (const jsRes of verification.javaScriptResource) {
        if (!jsRes.uri) {
          continue
        }
        if (jsRes.apiFramework == 'omid') {
          jsRes.verificationParameters = verification.verificationParameters
          try {
            this.handleOmidVerification(jsRes, verification.vendor, ad)
          } catch (err) {
            logOmidError('Error in OMID verification', err, { adId: ad.adId })
          }
        }
      }
    }
  }

  /**
   * Handles OMID verification for a given ad, setting up the OMID session and firing required events
   * @param jsRes - The JavaScript resource to verify
   * @param vendor - The vendor of the verification
   * @param ad - The ad object
   */
  handleOmidVerification(
    jsRes: OmidVerificationResource,
    vendor: string,
    ad: OmidAd
  ) {
    if (!window.OmidSessionClient?.default) {
      logOmidError('OmidSessionClient is not available on window.')
      return
    }
    // Create a verification script resource for OMID using the provided resource, vendor, and parameters
    const verificationScriptResource =
      new window.OmidSessionClient.default.VerificationScriptResource(
        jsRes.uri,
        vendor || '',
        jsRes.verificationParameters || ''
      )
    // Define the OMID partner (the integration name and version)
    const partner = { name: 'ReshetOTTPlayer', version: '1.0.0' }
    // Create the OMID context and attach the video element and verification resources
    const context = new window.OmidSessionClient.default.Context(
      partner
    ) as unknown as OmidContext
    context.videoElement = this.videoElement
    context.verificationScriptResources = [verificationScriptResource]
    context.supports = ['video']
    context.accessMode = 'full'
    context.integration = 'html5'
    // Ensure the OMID service iframe exists in the DOM
    this.ensureOmidServiceIframe()
    const omidServiceIframe = document.getElementById(
      'omid-service'
    ) as HTMLIFrameElement
    // Attach the OMID service window to the context if available
    if (omidServiceIframe?.contentWindow) {
      if (typeof context.setServiceWindow == 'function') {
        context.setServiceWindow(omidServiceIframe.contentWindow)
      } else {
        context.serviceWindow = omidServiceIframe.contentWindow
      }
    }
    try {
      // Clean up any previous OMID session for this ad
      this.cleanupAdSession(ad.adId)
      this.cleanedUpAdIds.delete(ad.adId)
      // Create a new OMID AdSession with the context
      const newSession = new window.OmidSessionClient.default.AdSession(
        context
      ) as unknown as OmidAdSession
      try {
        // Set creative and impression types, then start the session
        newSession.setCreativeType('video')
        newSession.setImpressionType('beginToRender')
        newSession.start()
      } catch (error) {
        // Log error if session properties cannot be set
        logOmidError('Error setting session properties', error, {
          adId: ad.adId,
        })
        return
      }
      // Store the session in the omidSessionRanges map for tracking
      const range = this.omidSessionRanges.get(ad.adId)
      if (range) {
        range.session = newSession
      } else {
        this.omidSessionRanges.set(ad.adId, {
          end: ad.adId,
          session: newSession,
        })
      }
      // Attach AdEvents and MediaEvents to the session for OMID event tracking
      newSession.adEvents = new window.OmidSessionClient.default.AdEvents(
        newSession
      ) as OmidAdEvents
      newSession.mediaEvents = new window.OmidSessionClient.default.MediaEvents(
        newSession
      ) as OmidMediaEvents
      // Register a session observer to handle OMID session lifecycle events
      newSession.registerSessionObserver((event: unknown) => {
        const evt = event as OmidSessionEvent
        if (evt.type == 'sessionStart') {
          try {
            // On session start, fire loaded, impression, and start media events again
            newSession.adEvents?.loaded()
            newSession.adEvents?.impressionOccurred()
            if (
              ad.durationInSeconds &&
              this.videoElement &&
              newSession.mediaEvents
            ) {
              newSession.mediaEvents.start(
                ad.durationInSeconds,
                this.videoElement.volume
              )
              // Add OMID quartile/completion event firing
              // Capture adStartTime as the current time when ad starts
              const adStartTime = this.videoElement.currentTime
              this._setupOmidQuartileTracking(
                newSession,
                ad,
                this.videoElement,
                adStartTime
              )
              // Add enhanced event tracking for play/pause, buffer start/finish, and volume change events
              this._setupEnhancedEventTracking(newSession, this.videoElement)
            }
          } catch (error) {
            logOmidError('Error firing OMID events after sessionStart', error, {
              adId: ad.adId,
            })
          }
        } else if (evt.type == 'sessionError') {
          logOmidError('Direct session error for ad', evt.data, {
            adId: ad.adId,
          })
        } else if (evt.type == 'sessionFinish') {
          this.cleanupAdSession(ad.adId)
        }
      })
    } catch (err) {
      // Handle any errors during the OMID verification process
      logOmidError('Error in direct verification', err, { adId: ad.adId })
      const range = this.omidSessionRanges.get(ad.adId)
      if (range && range.session) {
        try {
          // Attempt to finish the session if possible
          const session = range.session as OmidAdSession
          if (session.finish) {
            session.finish()
          }
        } catch (e) {
          logOmidError('Error cleaning up session on error', e, {
            adId: ad.adId,
          })
        }
        range.session = null
      }
    }
  }

  /**
   * Cleans up sessions that are out of range.
   * @param fragSn - The fragment sequence number to search for.
   */
  cleanupSessionsOutOfRange(fragSn: string) {
    for (const [adId, range] of this.omidSessionRanges.entries()) {
      if (fragSn > range.end) {
        this.cleanupAdSession(adId)
        this.omidSessionRanges.delete(adId)
      }
    }
  }

  /**
   * Cleans up an ad session.
   * @param adId - The ID of the ad to clean up.
   */
  cleanupAdSession(adId: string) {
    if (this.cleanedUpAdIds.has(adId)) {
      return
    }
    this.cleanedUpAdIds.add(adId)
    const range = this.omidSessionRanges.get(adId)
    if (range?.session && (range.session as OmidAdSession)?.finish) {
      try {
        const session = range.session as OmidAdSession
        if (session.finish) {
          session.finish()
        }
      } catch (e) {
        logOmidError('Error cleaning up session', e, { adId })
      }
      range.session = null
      this.omidSessionRanges.delete(adId)
    }
    setTimeout(() => {
      this.cleanedUpAdIds.delete(adId)
    }, 10000)
  }

  /**
   * Cleans up all sessions and resets the manager.
   */
  cleanup() {
    this.omidSessionRanges.forEach((range, adId) => {
      try {
        if (range.session && (range.session as OmidAdSession)?.finish) {
          const session = range.session as OmidAdSession
          if (session.finish) {
            session.finish()
          }
        }
      } catch (e) {
        logOmidError('Error cleaning up session', e, { adId })
      }
    })
    this.omidSessionRanges.clear()
    this.executedAdJs.clear()
    this.reportedAdEvents.clear()
    this.adTrackingData = null
  }

  /*
   * Private methods
   */

  /**
   * Finds an ad in the ad tracking data by its fragment sequence number.
   * @param fragSn - The fragment sequence number to search for.
   * @returns The ad object if found, otherwise null.
   */
  private findAdByFragmentSn(fragSn: string): OmidAd | null {
    if (!this.adTrackingData?.avails) {
      return null
    }
    for (const avail of this.adTrackingData.avails) {
      if (!avail.ads) {
        continue
      }
      for (const candidateAd of avail.ads) {
        if (candidateAd.adId == fragSn) {
          return candidateAd
        }
        if (candidateAd.trackingEvents) {
          for (const event of candidateAd.trackingEvents) {
            if (event.eventId == fragSn) {
              return candidateAd
            }
          }
        }
      }
    }
    return null
  }

  /**
   * Handles tracking events for an ad.
   * @param ad - The ad object to handle tracking events for.
   * @param fragSn - The fragment sequence number to search for.
   */
  private handleTrackingEvents(ad: OmidAd, fragSn: string) {
    if (!ad.trackingEvents?.length || !ad.clientReport) {
      if (!ad.clientReport) {
        console.log('Not reporting tracking events for ad')
      }
      if (!ad.trackingEvents?.length) {
        console.log('Tracking events for ad not found')
      }
      return
    }

    console.log('Reporting tracking events for ad')
    ad.trackingEvents.forEach(eventObj => {
      const eventKey = `${ad.adId}:${eventObj.eventType}`
      if (eventObj.eventId != fragSn) {
        return
      }
      if (!eventObj.beaconUrls?.length) {
        return
      }
      if (this.reportedAdEvents.has(eventKey)) {
        return
      }
      eventObj.beaconUrls.forEach(async url => {
        try {
          await fetch(url, { method: 'GET', mode: 'no-cors' })
        } catch (err) {
          logOmidError('Beacon error', err, { url, adId: ad.adId })
        }
      })
      this.reportedAdEvents.add(eventKey)
    })
  }

  /**
   * Ensures the OMID service iframe is present in the DOM.
   * @returns The OMID service iframe element.
   */
  private ensureOmidServiceIframe() {
    const iframe = document.getElementById(
      'omid-service'
    ) as HTMLIFrameElement | null
    if (iframe) {
      return iframe
    }
    const newIframe = document.createElement('iframe')
    newIframe.id = 'omid-service'
    newIframe.name = 'omid-service'
    newIframe.style.display = 'none'
    newIframe.sandbox.add('allow-scripts')
    newIframe.sandbox.add('allow-same-origin')
    newIframe.src = 'about:blank'
    document.body.appendChild(newIframe)
    return newIframe
  }

  /**
   * Sets up OMID quartile and completion event firing for the ad session.
   * Follows Open Measurement (OMID) spec for MediaEvents.
   */
  private _setupOmidQuartileTracking(
    session: unknown,
    ad: OmidAd,
    videoElement: HTMLVideoElement,
    adStartTime: number
  ) {
    const s = session as OmidAdSession
    if (!ad.durationInSeconds || !videoElement || !s.mediaEvents) {
      return
    }
    const duration = ad.durationInSeconds
    const fired = {
      q1: false,
      q2: false,
      q3: false,
      complete: false,
      skipped: false,
    }

    // Track original and last-adjusted volume for quartile-based adjustments
    const originalVolume = videoElement.muted ? 0 : videoElement.volume
    let lastAdjustedVolume = originalVolume
    const volumeDelta = 0.1

    const adjustVolumeAtQuartile = () => {
      if (videoElement.muted) {
        return
      }
      const target =
        lastAdjustedVolume >= originalVolume
          ? Math.max(0, lastAdjustedVolume - volumeDelta)
          : Math.min(1, lastAdjustedVolume + volumeDelta)

      try {
        videoElement.volume = target
      } catch (e) {
        logOmidError('Error setting volume', e, { adId: ad.adId })
      }
      try {
        if (s.mediaEvents && typeof s.mediaEvents.volumeChange === 'function') {
          s.mediaEvents.volumeChange(target)
        }
      } catch (e) {
        logOmidError('Error firing volumeChange event', e, { adId: ad.adId })
      }
      lastAdjustedVolume = target
    }

    // Handler for quartile events
    const timeUpdateHandler = () => {
      const currentAdTime = videoElement.currentTime - adStartTime
      if (currentAdTime < 0) {
        return
      } // Ad hasn't started yet
      if (!fired.q1 && currentAdTime >= duration * 0.25) {
        s.mediaEvents?.firstQuartile()
        adjustVolumeAtQuartile()
        fired.q1 = true
      }
      if (!fired.q2 && currentAdTime >= duration * 0.5) {
        s.mediaEvents?.midpoint()
        adjustVolumeAtQuartile()
        fired.q2 = true
      }
      if (!fired.q3 && currentAdTime >= duration * 0.75) {
        s.mediaEvents?.thirdQuartile()
        adjustVolumeAtQuartile()
        fired.q3 = true
      }
      if (!fired.complete && currentAdTime >= duration * 0.98) {
        // 98% to ensure event fires before video ends
        s.mediaEvents?.complete()

        // Restore original volume at completion and report
        if (!videoElement.muted) {
          try {
            videoElement.volume = originalVolume
          } catch (e) {
            logOmidError('Error setting volume', e, { adId: ad.adId })
          }
          try {
            if (
              s.mediaEvents &&
              typeof s.mediaEvents.volumeChange === 'function'
            ) {
              s.mediaEvents.volumeChange(originalVolume)
            }
          } catch (e) {
            logOmidError('Error firing volumeChange event', e, {
              adId: ad.adId,
            })
          }
        }

        fired.complete = true
        videoElement.removeEventListener('timeupdate', timeUpdateHandler)
      }
    }

    videoElement.addEventListener('timeupdate', timeUpdateHandler)

    // Clean up listeners if session finishes early
    const cleanup = () => {
      videoElement.removeEventListener('timeupdate', timeUpdateHandler)
    }
    if (typeof s.registerSessionObserver === 'function') {
      s.registerSessionObserver((event: unknown) => {
        const evt = event as OmidSessionEvent
        if (evt.type == 'sessionFinish') {
          cleanup()
        }
      })
    }
  }

  /**
   * OMID event tracking for play/pause, buffer start/finish, and volume change events.
   */
  private _setupEnhancedEventTracking(
    session: unknown,
    videoElement: HTMLVideoElement
  ) {
    const s = session as OmidAdSession
    if (!videoElement || !s.mediaEvents) {
      return
    }

    // Track play/pause state
    let isPlaying = false
    let lastVolume = videoElement.volume

    // Play/Pause event handlers
    const playHandler = () => {
      if (!isPlaying) {
        isPlaying = true
        s.mediaEvents?.resume()
      }
    }

    const pauseHandler = () => {
      if (isPlaying) {
        isPlaying = false
        s.mediaEvents?.pause()
      }
    }

    // Volume change handler
    const volumeChangeHandler = () => {
      const currentVolume = videoElement.volume
      if (currentVolume !== lastVolume) {
        lastVolume = currentVolume
        s.mediaEvents?.volumeChange(currentVolume)
      }
    }

    const bufferStartHandler = () => {
      s.mediaEvents?.bufferStart()
    }

    const bufferFinishHandler = () => {
      s.mediaEvents?.bufferFinish()
    }

    // Add event listeners
    videoElement.addEventListener('play', playHandler)
    videoElement.addEventListener('pause', pauseHandler)
    videoElement.addEventListener('volumechange', volumeChangeHandler)
    videoElement.addEventListener('waiting', bufferStartHandler)
    videoElement.addEventListener('canplay', bufferFinishHandler)

    // Cleanup function
    const cleanup = () => {
      videoElement.removeEventListener('play', playHandler)
      videoElement.removeEventListener('pause', pauseHandler)
      videoElement.removeEventListener('volumechange', volumeChangeHandler)
      videoElement.removeEventListener('waiting', bufferStartHandler)
      videoElement.removeEventListener('canplay', bufferFinishHandler)
    }

    // Register cleanup with session observer
    if (typeof s.registerSessionObserver === 'function') {
      s.registerSessionObserver((event: unknown) => {
        const evt = event as OmidSessionEvent
        if (evt.type == 'sessionFinish') {
          cleanup()
        }
      })
    }

    // Return cleanup function for manual cleanup if needed
    return cleanup
  }
}

export { OmidManager }

declare global {
  interface Window {
    OmidSessionClient?: {
      default: {
        VerificationScriptResource: new (
          uri: string,
          vendor: string,
          parameters: string
        ) => unknown
        Context: new (partner: { name: string; version: string }) => unknown
        AdSession: new (context: unknown) => unknown
        AdEvents: new (session: unknown) => unknown
        MediaEvents: new (session: unknown) => unknown
      }
    }
  }
}

const adEvents = [
  'adstarted',
  'adpaused',
  'adresumed',
  'adfirstquartile',
  'admidpoint',
  'adthirdquartile',
  'adskipped',
  'adcompleted',
  'adbreakstart',
  'adbreakend',
]
// const volumeEvents = new Set([
//   'adfirstquartile',
//   'admidpoint',
//   'adthirdquartile',
// ])

export const setOmidEvents = (kp: HTMLVideoElement) => {
  let origVol: number | null = null
  adEvents.forEach(
    ev =>
      kp &&
      kp.addEventListener &&
      kp.addEventListener(ev, () => {
        if (!kp.muted) {
          const volumeChange = 0.1

          if (!origVol) {
            origVol = kp.volume as number
          }

          // Determine target volume (alternate between increase/decrease)
          const targetVolume =
            kp.volume >= origVol
              ? Math.max(0, kp.volume - volumeChange)
              : Math.min(1, kp.volume + volumeChange)

          try {
            switch (ev) {
              case 'adstarted':
                setTimeout(() => {
                  kp.volume = targetVolume
                }, 1000)
                break
              case 'adfirstquartile':
              case 'admidpoint':
              case 'adthirdquartile':
                kp.volume = targetVolume
                break
              default:
                break
            }
          } catch (e) {
            console.error(e)
          }
        }
      })
  )
}
// export const removeOmidEvents = (kp: any) => {
//   adEvents.forEach(
//     ev => kp && kp.removeEventListener && kp.removeEventListener(ev)
//   )
// }
